import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Avatar, Input, Tabs, Button, Modal, message, Badge, Typography, Spin } from 'antd';
import { ExclamationCircleOutlined, MenuFoldOutlined, MenuUnfoldOutlined, LoadingOutlined } from '@ant-design/icons';
import { BsPersonCircle } from 'react-icons/bs';
import { IoIosSearch } from "react-icons/io";
import { formatDistanceToNow } from 'date-fns';
import { MdOutlineDeleteSweep } from "react-icons/md";
const { Search } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;
const { confirm } = Modal;

const ChatSidebar = ({ chats = [], onChatSelect, selectedChat, user, onDeleteChat, onScrollBottom, loadingMore, hasMore, chatSidebarRef, onTabChange }) => {
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('direct');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const listContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      if (!mobile) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scrolling to detect when user scrolls to bottom
  const handleScroll = useCallback(() => {
    if (!listContainerRef.current) {
      console.log('Missing listContainerRef');
      return;
    }
    
    if (!hasMore) {
      console.log('No more chats to load');
      return;
    }
    
    if (loadingMore) {
      console.log('Already loading more chats');
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
    console.log('Scroll metrics:', { scrollTop, scrollHeight, clientHeight });
    
    // Calculate scroll percentage (0 to 1)
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    console.log('Scroll percentage:', scrollPercentage.toFixed(2));
    
    // If scrolled past 80% of the total height, trigger loading more
    if (scrollPercentage >= 0.8) {
      console.log('*** SCROLL THRESHOLD REACHED: 80% ***');
      if (onScrollBottom) {
        onScrollBottom();
      }
    }
  }, [onScrollBottom, hasMore, loadingMore]);

  useEffect(() => {
    const listContainer = listContainerRef.current;
    if (listContainer) {
      console.log('Setting up scroll listener');
      listContainer.addEventListener('scroll', handleScroll);
      
      // Initial check - if not enough content to scroll, load more
      const { scrollHeight, clientHeight } = listContainer;
      console.log('Initial scroll metrics:', { scrollHeight, clientHeight });
      
      if (scrollHeight <= clientHeight && hasMore && !loadingMore) {
        console.log('Not enough content to scroll, loading more immediately');
        onScrollBottom && onScrollBottom();
      }
      
      return () => {
        console.log('Removing scroll listener');
        listContainer.removeEventListener('scroll', handleScroll);
      };
    } else {
      console.log('Missing listContainer ref');
    }
  }, [handleScroll, hasMore, loadingMore, onScrollBottom]);
  
  // Allow parent to access the container ref
  useEffect(() => {
    if (chatSidebarRef) {
      chatSidebarRef.current = listContainerRef.current;
    }
  }, [chatSidebarRef]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // Ensure chats is an array and filter out any undefined/null items
  const validChats = Array.isArray(chats) ? chats.filter(Boolean) : [];

  const filteredChats = validChats.filter(chat => {
    const searchLower = searchText?.toLowerCase() || '';
    const otherUser = chat?.users?.find(u => {
      // In a group chat, we don't need to find the other user
      if (chat?.isGroupChat) return false;
      
      // For 1-on-1 chats, find the user that's not the current user
      return u?._id !== user?.id;
    });
    
    const chatName = chat?.isGroupChat 
      ? chat?.chatName?.toLowerCase()
      : otherUser ? `${otherUser?.firstName} ${otherUser?.lastName}`.toLowerCase() : '';
    return chatName?.includes(searchLower);
  });

  // Filter chats based on tab
  const displayedChats = filteredChats.filter(chat => 
    
    (activeTab === 'direct' && !chat?.isGroupChat) ||
    (activeTab === 'groups' && chat?.isGroupChat)
  );

  const showDeleteConfirm = (chatId, isGroupChat) => {
    confirm({
      title: isGroupChat ? 'Are you sure you want to left the group?': 'Are you sure you want to delete this chat?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        handleDeleteChat(chatId);
      },
    });
  };

  const handleDeleteChat = async (chatId) => {
    try {
      onDeleteChat(chatId); // Just notify parent, let parent handle the API call
    } catch (error) {
      console.error('Error deleting chat:', error);
      message.error('Failed to delete chat');
    }
  };

  const handleChatSelect = (chat) => {
    onChatSelect(chat);
    if (isMobileView) {
      setSidebarVisible(false);
    }
  };

  const renderChatItem = (chat) => {
    const isDeletedForUser = chat?.deletedFor?.includes(user?._id); // Check if the chat is deleted for the user
    const otherUsers = chat?.users?.filter((u) => u._id !== user?._id); // Exclude current user
  
    const chatName = chat.isGroupChat
      ? chat.chatName
      : otherUsers?.map((u) => `${u.firstName} ${u.lastName}`).join(', ') || "Unknown User";
  
    const lastMessage = chat?.latestMessage
      ? chat.latestMessage.content
      : "No messages yet";
      const hasUnreadMessages = chat?.unreadCount > 0; // Define hasUnreadMessages
    return (
      <List.Item
        key={chat?._id}
        className={`${selectedChat?._id === chat?._id ? 'selected' : ''} ${hasUnreadMessages ? 'unread' : ''}`}
        onClick={() => handleChatSelect(chat)}
        actions={[
          <Button
            type="text"
            icon={<MdOutlineDeleteSweep  />}
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              showDeleteConfirm(chat?._id, chat?.isGroupChat);
            }}
          />
        ]}
      >
        <List.Item.Meta
          avatar={
            <Badge
              count={isDeletedForUser ? 'Deleted' : 0} // Add 'D' badge for deleted chats
              style={{
                backgroundColor: isDeletedForUser ? 'red' : 'transparent',
              }}
            >
              <Avatar
                size={30}
                icon={<BsPersonCircle size={24} />}
                src={chat.isGroupChat ? chat.groupAvatar : otherUsers[0]?.profilePicture}
              />
            </Badge>
          }
          title={
            <div className="chat-item-header">
              <div className="chat-name-container">
                <span>{chatName}</span>
                {hasUnreadMessages && (
                  <Badge 
                    count={chat.unreadCount} 
                    style={{ 
                      backgroundColor: '#1890ff',
                      marginLeft: '8px'
                    }} 
                  />
                )}
              </div>
            </div>
          }
          description={
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              color: hasUnreadMessages ? '#1890ff' : 'rgba(0, 0, 0, 0.45)',
              fontWeight: hasUnreadMessages ? 500 : 400
            }}>
              <Text type="secondary" ellipsis>
                {lastMessage}
              </Text>
              {chat.latestMessage && chat.latestMessage.createdAt && (
                <Text type="secondary" className="message-time" style={{ marginLeft: 'auto' }}>
                  {(() => {
                    try {
                      return formatDistanceToNow(new Date(chat.latestMessage.createdAt), { addSuffix: true });
                    } catch (error) {
                      console.error('Error formatting date:', error);
                      return '';
                    }
                  })()}
                </Text>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <div className={`chat-sidebar ${isMobileView ? (sidebarVisible ? 'show' : '') : ''}`}>
      {isMobileView && (
        <Button
          type="text"
          icon={sidebarVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          onClick={toggleSidebar}
          className="sidebar-toggle"
          style={{
            position: 'absolute',
            right: '-40px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            borderRadius: '0 4px 4px 0',
            width: 40,
            height: 40,
            zIndex: 1
          }}
        />
      )}
      <div className="chat-sidebar-wrapper">
        <div className="sidebar-header">
         
          <Search
            placeholder="Search chats..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<IoIosSearch size={16} color='pink'/>}
            allowClear
            className="search-input"
          />
          <Tabs 
            activeKey={activeTab} 
            onChange={(key) => {
              setActiveTab(key);
              if (onTabChange) onTabChange(key);
            }}
            className="chat-tabs"
          >
            <TabPane tab="Direct Messages" key="direct" />
            <TabPane tab="Groups" key="groups" />
          </Tabs>
        </div>
        <div 
          className="sidebar-content" 
          ref={listContainerRef}
          style={{ 
            height: '500px',
            maxHeight: '67vh', 
            overflowY: 'auto',
            paddingBottom: '20px'
          }}>
          <List
            dataSource={displayedChats}
            renderItem={renderChatItem}
            locale={{
              emptyText: 'No conversations found'
            }}
          />
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              <div style={{ marginTop: 8 }}>Loading more...</div>
            </div>
          )}
          {!hasMore && displayedChats.length > 0 && (
            <div style={{ textAlign: 'center', padding: '10px', color: '#999' }}>
              No more chats to load
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
